import NProgress from 'nprogress' // progress bar
import 'nprogress/nprogress.css' // progress bar style
import store from './store'
import router from './router'
import { ADS_GA_EVENTS, emitAdsEvent } from './utils/analytics'
import { getToken, setToken } from '@/utils/auth' // get token from cookie
import getPageTitle from '@/utils/get-page-title'
import {
  detectCurrentLocale,
  fetchMessagesTranslations,
  translate,
} from '@/utils/i18n'
import { isArray } from '@/utils/validate'
import { handleRedirectOnboardingV2 } from '@/utils/onboarding'
import locator from '@/service-locator'
import i18n from '@/i18n/index'
import { getABTProvider } from '@/plugins/abt/statsig'
import {
  vuePathToNextUrl,
  ADS_NEXTJS_APP_FEATURE_FLAG_NAME,
} from '@/utils/cross-app-routes'
import {
  isMerchantOrdersListV2Enabled,
  ORDER_LIST_PATH_V1,
  ORDER_LIST_PATH_V2,
  ORDER_DETAIL_PATH,
  ORDER_DETAIL_PATH_V2,
  redirectToOrderListPath,
  redirectToOrderDetailPath,
} from '@/utils/order-routes'
import {
  isMerchantBusinessAddressV2Enabled,
  ADDRESS_COLLECTION_PATH_V1,
  ADDRESS_COLLECTION_PATH_V2,
  redirectToAddressCollectionPath,
} from '@/utils/address-collection-routes'
import {
  isMerchantUserSettingsV2Enabled,
  USER_SETTINGS_PATH_V1,
  USER_SETTINGS_PATH_V1_ROOT,
  USER_SETTINGS_PATH_V2,
  redirectToUserSettingsPath,
} from '@/utils/user-settings-routes'
import {
  isMerchantExportedRequestsV2Enabled,
  EXPORTED_LIST_PATH_V1,
  EXPORTED_LIST_PATH_V2,
  redirectToExportedListPath,
} from '@/utils/export-routes'

const ORDER_DETAIL_V1_PREFIX = ORDER_DETAIL_PATH.replace('/:orderId', '')
const ORDER_DETAIL_V2_PREFIX = ORDER_DETAIL_PATH_V2.replace('/:orderId', '')

NProgress.configure({ showSpinner: false }) // NProgress Configuration

const whiteList = [
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/user-email-verification',
  '/onboarding',
] // no redirect whitelist

const onboardingRoutes = ['/login', '/onboarding']

const kycRoutes = [
  '/profile',
  '/kyc',
  '/kyc-business',
  '/kyc-legal',
  '/kyc-bank',
  '/kyc-contact',
  '/kyc-additional-info',
]

function _findFirstMenuRoute(permissionRoutes) {
  return permissionRoutes.find(
    (route) =>
      route.meta && !route.meta.isKyc && !route.disabled && !route.hidden
  )
}

function _findPermissionRoute(permissionRoutes, targetRoute) {
  if (!isArray(permissionRoutes)) {
    return null
  }

  return permissionRoutes.find((route) => {
    if (
      route.path === targetRoute.path ||
      targetRoute.matched?.some(
        (matchedRoute) => matchedRoute.path === route.path
      )
    ) {
      return true
    }

    if (isArray(route.children)) {
      return _findPermissionRoute(route.children, targetRoute)
    }

    return false
  })
}

function _getFirstAvailableRoute(permissionRoutes) {
  const firstMenuRoute = _findFirstMenuRoute(permissionRoutes)

  if (firstMenuRoute) {
    return firstMenuRoute
  }

  const profileRoute = _findPermissionRoute(permissionRoutes, '/profile')

  if (profileRoute) {
    return profileRoute
  }

  return null
}

function _handleResolveToPermissionRoute(routeTo, next) {
  const permissionRoutes = store.getters.permissionRoutes

  const foundPermissionRoute = _findPermissionRoute(permissionRoutes, routeTo)

  // An available route
  if (foundPermissionRoute) {
    return next()
  }

  const firstAvailableRoute = _getFirstAvailableRoute(permissionRoutes)

  if (firstAvailableRoute) {
    return next({ path: firstAvailableRoute.path })
  }

  return next({ path: '/401', query: { redirect: routeTo.fullPath } })
}

router.beforeEach(async (to, from, next) => {
  // start progress bar
  NProgress.start()

  if (
    isMerchantOrdersListV2Enabled() &&
    (to.path === ORDER_LIST_PATH_V1 || to.path === ORDER_LIST_PATH_V2)
  ) {
    redirectToOrderListPath(to)
    return
  }

  if (
    isMerchantOrdersListV2Enabled() &&
    (to.path.startsWith(`${ORDER_DETAIL_V1_PREFIX}/`) ||
      to.path.startsWith(`${ORDER_DETAIL_V2_PREFIX}/`))
  ) {
    redirectToOrderDetailPath(to)
    return
  }

  if (
    isMerchantBusinessAddressV2Enabled() &&
    (to.path === ADDRESS_COLLECTION_PATH_V1 ||
      to.path === ADDRESS_COLLECTION_PATH_V2)
  ) {
    redirectToAddressCollectionPath(to)
    return
  }

  if (
    isMerchantUserSettingsV2Enabled() &&
    (to.path === USER_SETTINGS_PATH_V1 ||
      to.path === USER_SETTINGS_PATH_V1_ROOT ||
      to.path === USER_SETTINGS_PATH_V2)
  ) {
    redirectToUserSettingsPath(to)
    return
  }

  if (
    isMerchantExportedRequestsV2Enabled() &&
    (to.path === EXPORTED_LIST_PATH_V1 || to.path === EXPORTED_LIST_PATH_V2)
  ) {
    redirectToExportedListPath(to)
    return
  }

  // If navigating to an ads route and the Next.js app is enabled,
  // redirect early to avoid unnecessary API calls (profile, permissions)
  if (
    /^\/ads/.test(to.path) &&
    getABTProvider().isFeatureEnabled(ADS_NEXTJS_APP_FEATURE_FLAG_NAME) &&
    process.env.NODE_ENV !== 'development'
  ) {
    const nextUrl = vuePathToNextUrl(to.path)
    const query =
      to.query && Object.keys(to.query).length
        ? '?' + new URLSearchParams(to.query).toString()
        : ''
    window.location.replace(
      window.location.origin + (nextUrl || '/ads/dashboard') + query
    )
    return
  }

  const underMaintenance = store.getters['ads/underMaintenance']

  if (
    to.path !== '/ads/dashboard' &&
    /^\/ads/.test(to.path) &&
    underMaintenance
  ) {
    next('/ads/dashboard')
    return
  }

  if (to.path === '/auth/admin') {
    if (to.query.token) {
      const hasToken = getToken()
      if (hasToken) {
        await store.dispatch('user/logout')
      }
      setToken(to.query.token)
      store.dispatch('user/setToken', to.query.token)
      try {
        const data = await store.dispatch('userProfile/getMerchantUserProfile')
        const targetPath = data?.status !== 'active' ? '/' : '/dashboard/home'

        next({ path: targetPath })
        NProgress.done()
      } catch (err) {
        NProgress.done()
      }
      return
    }
  }

  // Detect locale and fetch messages if locale changed.
  const locale = detectCurrentLocale(to)

  if (
    !store.getters['translations/hasLocale'] ||
    locale !== store.getters['translations/currentLocale']
  ) {
    await fetchMessagesTranslations(locale)
  }

  // set page title
  document.title = getPageTitle(translate(to.meta.title))

  // determine whether the user has logged in
  const hasToken = getToken()
  if (hasToken) {
    // Validate token
    try {
      await store.dispatch('userProfile/getMerchantUserProfile')
    } catch (error) {
      await store.dispatch('user/logout')
      // go to login page to re-login
      next(`/login?redirect=${to.path}`)
      window.location.reload()
      NProgress.done()
      return
    }

    const userProfile = store.getters['userProfile/profile']
    if (userProfile && userProfile.role === 'MERCHANT_KYC_USER') {
      if (kycRoutes.includes(to.path)) {
        next()
      } else {
        next('/kyc')
      }
    }

    if (
      userProfile &&
      userProfile.onboardingId !== null &&
      userProfile.role === 'MERCHANT_ONBOARDING_USER'
    ) {
      if (onboardingRoutes.includes(to.path)) {
        next()
      }
      // TODO: Redirect to new onboarding page (business.tamara.co). Wait for them to implement it fully
      else if (userProfile?.version === 'v2') {
        handleRedirectOnboardingV2(locator.store.state?.user?.token)
      } else {
        next('/onboarding')
      }
      return
    }

    if (to.path === '/login') {
      // if is logged in, redirect to the home page
      next({ path: '/' })
      NProgress.done()
    } else {
      // determine whether the user has obtained his permission roles through getInfo
      const userRoles = store.getters.roles
      const hasRoles = userRoles && userRoles.length > 0
      const permissionAdded = store.getters.permissionAdded

      if (hasRoles && permissionAdded) {
        return _handleResolveToPermissionRoute(to, next)
      }

      try {
        // Validate user. Set roles and routes: Note:: We don't need this in MVP version
        // get user info
        // note: roles must be a object array! such as: ['admin'] or ,['developer','editor']
        const { permissions, role } = await store.dispatch(
          'userProfile/getMerchantUserProfile'
        )

        if (!['MERCHANT_KYC_USER', 'MERCHANT_ONBOARDING_USER'].includes(role)) {
          await store.dispatch('profile/getProfile')
        }
        // generate accessible routes map based on roles
        const accessRoutes = await store.dispatch(
          'permission/generateRoutes',
          permissions
        )
        // dynamically add accessible routes
        router.addRoutes(accessRoutes)
        // hack method to ensure that addRoutes is complete
        // set the "replace: true", so the navigation will not leave a history record
        next({ ...to, replace: true })
      } catch (error) {
        // fully logout and remove cached data and go to login page to re-login
        await store.dispatch('user/logout')
        next(`/login?redirect=${to.path}`)
        window.location.reload()
        NProgress.done()
      }
    }
  } else {
    /* has no token */
    if (whiteList.indexOf(to.path) !== -1) {
      // in the free login whitelist, go directly
      next()
    } else {
      // other pages that do not have permission to access are redirected to the login page.
      next(`/login?redirect=${to.path}`)
      NProgress.done()
    }
  }
})

const isAdTecPath = (path) => path.startsWith('/ads')
const isDealsPath = (path) => path.startsWith('/deals')

router.afterEach((to, from) => {
  store.dispatch('route/changeRoute', { to, from })

  // GA event will remove later
  if (isAdTecPath(to.path)) {
    emitAdsEvent(ADS_GA_EVENTS.PAGE_VIEW, {
      page_path: to.path,
      page_title: document.title,
    })
  }

  if (isAdTecPath(to.path)) {
    locator.tracking.event('ads_page_view', {
      page_path: to.path,
      page_title: document.title,
    })
  }

  if (isDealsPath(to.path)) {
    locator.tracking.event('deals_page_viewed', {
      page_path: to.path,
      page_title: document.title,
      page_tab: i18n.t(to.meta.title),
    })
  }
  // finish progress bar
  NProgress.done()
})
